<script setup>
import { ref, nextTick } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KnowledgeBaseAPI from 'dashboard/api/knowledgeBase';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const dialogRef = ref(null);
const query = ref('');
const hits = ref([]);
const searched = ref(false);
const isLoading = ref(false);

const open = () => {
  query.value = '';
  hits.value = [];
  searched.value = false;
  isLoading.value = false;
  nextTick(() => {
    dialogRef.value?.open();
  });
};

const close = () => {
  dialogRef.value?.close();
};

const runSearch = async () => {
  const q = query.value.trim();
  if (!q) return;

  isLoading.value = true;
  searched.value = true;
  try {
    const res = await KnowledgeBaseAPI.testSearch(q);
    hits.value = res.data?.hits || [];
  } catch (error) {
    useAlert(error.response?.data?.error || error.message || '检索测试失败');
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    title="AI 检索命中测试"
    cancel-button-label="关闭"
    :show-confirm-button="false"
    width="2xl"
  >
    <div class="flex flex-col gap-4 py-2">
      <!-- 搜索输入 -->
      <div class="flex items-center gap-2">
        <Input
          v-model="query"
          type="text"
          placeholder="模拟客户提问，例如：发货后几天能收到？"
          class="flex-1 min-w-0 [&>input]:!rounded-[0.625rem]"
          size="sm"
          @keyup.enter="runSearch"
        />
        <Button
          :label="isLoading ? '检索中…' : '测试检索'"
          :is-loading="isLoading"
          :disabled="isLoading || !query.trim()"
          color="blue"
          size="sm"
          @click="runSearch"
        />
      </div>

      <!-- 结果列表 -->
      <div v-if="searched" class="flex flex-col gap-3 mt-1">
        <div class="flex items-center justify-between text-xs text-n-slate-10">
          <span>检索匹配结果</span>
          <span>共找到 {{ hits.length }} 条相关知识</span>
        </div>

        <div v-if="hits.length === 0" class="py-8 text-center text-xs text-n-slate-10 border rounded-lg bg-n-alpha-1 border-n-weak">
          未检索到相关内容，AI 将判断为「超出知识库范畴」并礼貌回复或建议转人工
        </div>

        <div v-else class="flex flex-col gap-2.5 max-h-96 overflow-y-auto">
          <div
            v-for="(hit, idx) in hits"
            :key="idx"
            class="p-3.5 border rounded-lg bg-n-solid-2 border-n-weak flex flex-col gap-1.5 shadow-xs"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs font-semibold text-n-slate-12">
                #{{ idx + 1 }} {{ hit.title || '文档片段' }}
              </span>
              <div class="flex items-center gap-2">
                <span
                  class="px-2 py-0.5 text-xs rounded-full font-medium"
                  :class="hit.kind === 'faq' ? 'bg-n-brand/10 text-n-blue-11 border border-n-brand/20' : 'bg-n-alpha-2 text-n-slate-11 border border-n-weak'"
                >
                  {{ hit.kind === 'faq' ? 'FAQ' : '文档' }}
                </span>
                <span
                  v-if="hit.score !== undefined"
                  class="text-xs px-1.5 py-0.5 rounded font-mono font-medium"
                  :class="hit.score >= 0.75 ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20'"
                >
                  相似度 {{ Math.round(hit.score * 100) }}%
                </span>
              </div>
            </div>

            <p class="text-xs text-n-slate-11 leading-relaxed">
              {{ hit.content }}
            </p>

            <div v-if="hit.answer" class="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 p-2 rounded mt-1">
              <strong>参考答案：</strong>{{ hit.answer }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </Dialog>
</template>
